import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, PageTitle } from '../ui';
import { ArrowLeft, FileText, Loader2, Download, Eye, Save } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCrm } from '../store';
import { withDemoFlag } from '../demoMode';
import { genererContratPdf, previewContratHtml } from '../contract/generatePdf';
import { renderContratHTML } from '../contract/renderContratHTML';
import type { Structure } from '../contract/content';
import { montantEnLettres } from '../contract/numberToWords';
import { renderPropositionHTML, rowToProp, type Proposition } from './Propositions';
import { exportHtmlsToPdf } from '../utils/pdfExport';
import { stripCpVille } from '@/crm/utils/adresse';

// Convertit une date FR (JJ/MM/AAAA) en ISO (AAAA-MM-JJ). Retourne null si invalide.
const frToIso = (fr?: string | null): string | null => {
  if (!fr) return null;
  const m = fr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const STRUCTURE_OPTIONS: { code: Structure; label: string; sub: string }[] = [
  { code: 'cygnature', label: 'CYGNATURE PRODUCTION', sub: 'SASU — SIRET 89847396200014' },
  { code: 'suivez_vos_reves', label: 'SUIVEZ VOS RÊVES EVENT', sub: 'Association loi 1901 — W103009117' },
];

interface FormState {
  client_nom: string; client_adresse: string; client_cp_ville: string; client_representant: string;
  client_tel: string; client_email: string; client_siret: string;
  contact_sur_place: string;
  devis_numero: string; devis_date: string;
  event_intitule: string;
  date_debut: string; heure_debut: string; date_fin: string; heure_fin: string;
  lieu: string;
  cb_location: boolean; cb_services: boolean; cb_artistique: boolean;
  cb_decoration: boolean; cb_photobooth: boolean; cb_technique: boolean;
  cb_wedding_planner: boolean;
  autre_nature: string;
  type_event: string; type_domaine: string;
  materiel: string; services: string; contraintes: string;
  exclus: string; livrables: string;
  acces: string; stationnement: string; electricite: string; stockage: string;
  role_producteur: boolean; role_diffuseur: boolean; role_prestataire: boolean;
  role_intermediaire: boolean; role_loueur: boolean; role_detail: string;
  responsable_remise: string; responsable_retour: string;
  inv_annexe: boolean; inv_photos: boolean; inv_bon: boolean;
  loge_espace: string;
  photobooth_formule: string; photobooth_numerique: string; photobooth_impressions: string;
  photobooth_personnalisation: string; photobooth_ia: string; photobooth_installation: string;
  montant_ht: string; montant_tva: string; montant_ttc: string;
  montant_ttc_lettres: string;
  modalites_paiement: string;
  caution: string; caution_forme: string; caution_restitution: string;
  piece_identite: string;
  cb_cheque_garantie: boolean; cheque_garantie_montant: string;


  cb_nettoyage_oui: boolean; cb_nettoyage_non: boolean;
  autorisations: string;
  collations: string; repas: string; hebergements: string;
  particularites: string;
  date_signature: string; nom_signataire_client: string;
  // Annexe proposition (utilisée par le bouton « Propal+Contrat »)
  proposition_numero?: string;
  proposition_lignes?: { titre: string; description: string; quantite: number; prix_unitaire_ht: number; options?: { label: string; ht: number }[] }[];
  proposition_total_ht?: number;
  proposition_total_tva?: number;
  proposition_total_ttc?: number;
  echeancier_paiement?: { label?: string; date?: string; montant?: number | string | null }[];
}

const DEFAULT_PIECE_IDENTITE = "En cas de location : copie d'une pièce d'identité en cours de validité du signataire (et justificatif société si applicable).";

const EMPTY: FormState = {
  client_nom: '', client_adresse: '', client_cp_ville: '', client_representant: '',
  client_tel: '', client_email: '', client_siret: '',
  contact_sur_place: '',
  devis_numero: '', devis_date: '',
  event_intitule: '',
  date_debut: '', heure_debut: '', date_fin: '', heure_fin: '',
  lieu: '',
  cb_location: false, cb_services: false, cb_artistique: false,
  cb_decoration: false, cb_photobooth: false, cb_technique: false,
  cb_wedding_planner: false,
  autre_nature: '',
  type_event: '', type_domaine: '',
  materiel: '', services: '', contraintes: '',
  exclus: 'Tout ce qui n’est pas expressément mentionné aux présentes (notamment restauration, hébergement, autorisations administratives, sécurité du site).',
  livrables: 'Prestation réalisée sur site conformément au devis accepté.',
  acces: 'Accès véhicule au plus près du lieu d’installation, à confirmer par le Client avant l’événement.',
  stationnement: 'Emplacement réservé à proximité immédiate, à la charge du Client.',
  electricite: 'Alimentation 220 V / 16 A à proximité du point d’installation, fournie par le Client.',
  stockage: 'Espace sécurisé et abrité mis à disposition pour le matériel avant et après la prestation.',
  role_producteur: false, role_diffuseur: false, role_prestataire: true,
  role_intermediaire: false, role_loueur: false, role_detail: '',
  responsable_remise: 'CYGNATURE PRODUCTION', responsable_retour: 'CYGNATURE PRODUCTION',
  inv_annexe: false, inv_photos: false, inv_bon: false,
  loge_espace: '1 table / 2 chaises / Portant à vêtements',
  photobooth_formule: '', photobooth_numerique: '', photobooth_impressions: '',
  photobooth_personnalisation: '', photobooth_ia: '', photobooth_installation: '',
  montant_ht: '', montant_tva: '', montant_ttc: '', montant_ttc_lettres: '',
  modalites_paiement: '',
  caution: '', caution_forme: 'chèque', caution_restitution: 'après contrôle (72 h)',
  piece_identite: DEFAULT_PIECE_IDENTITE,
  cb_cheque_garantie: false, cheque_garantie_montant: '',

  cb_nettoyage_oui: false, cb_nettoyage_non: false,
  autorisations: '',
  collations: '', repas: '', hebergements: '',
  particularites: '',
  date_signature: new Date().toLocaleDateString('fr-FR'), nom_signataire_client: '',
};

type PropOption = {
  id: string;
  numero: string | null;
  prestation_id: string | null;
  client_id: string | null;
  statut: string;
  date_emission: string | null;
  date_event: string | null;
  mode_paiement: string | null;
  acompte_pct: number | null;
  acompte_jours: number | null;
  nb_echeances: number | null;
  echeancier_overrides: any;
};

type ContratOption = { label: string; ht: number };

const parseMoneyLike = (value: any) => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const isEnabledOption = (value: any) => value === true || value === 1 || value === '1' || String(value ?? '').toLowerCase() === 'true';

const cleanOptionLabel = (value: any) => String(value ?? '')
  .replace(/\((?=[^)]*(?:\d|€|eur|euro|euros|ht|ttc))[^)]*\)/gi, ' ')
  .replace(/\s*[:：-]\s*\d+[\d\s,.]*(?:€|eur|euro|euros|ht|ttc).*$/i, '')
  .replace(/\s+\d+[\d\s,.]*(?:€|eur|euro|euros|ht|ttc)\b.*$/i, '')
  .replace(/\s+/g, ' ')
  .trim();

const uniqueOptions = (options: ContratOption[]) => {
  const seen = new Set<string>();
  return options.filter((option) => {
    const label = cleanOptionLabel(option.label);
    if (!label) return false;
    const key = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    option.label = label;
    return true;
  });
};

const extractContratOptionsFromCalc = (calc: any): ContratOption[] => {
  if (!calc || typeof calc !== 'object') return [];
  const out: ContratOption[] = [];
  const notes: string[] = [];
  [1, 2, 3, 4].forEach((n) => {
    const label = cleanOptionLabel(
      calc[`opt${n}Lib`] ?? calc[`opt${n}Label`] ?? calc[`option${n}_libelle`] ?? calc[`option${n}Libelle`] ?? calc[`option${n}Lib`] ?? '',
    );
    const libre = parseMoneyLike(calc[`opt${n}HT`] ?? calc[`option${n}_ht`] ?? calc[`option${n}HT`]);
    const tarif = parseMoneyLike(calc[`opt${n}Tarif`] ?? calc[`option${n}_tarif`] ?? calc[`option${n}Tarif`]);
    const marge = parseMoneyLike(calc[`opt${n}Marge`] ?? calc[`option${n}_marge`] ?? calc[`option${n}Marge`]);
    const explicitOn = calc[`opt${n}On`] ?? calc[`option${n}_on`] ?? calc[`option${n}On`] ?? calc[`option${n}_checked`] ?? calc[`option${n}Checked`];
    // Règle métier contrat client : seules les options réellement cochées sont reprises.
    // Compat legacy : si l'ancien calculateur n'a pas stocké le booléen, une option nommée + chiffrée est considérée comme retenue.
    const enabled = isEnabledOption(explicitOn) || (explicitOn == null && !!label && (libre > 0 || tarif > 0));
    if (!enabled) return;
    const ht = libre > 0 ? libre : tarif * (1 + marge / 100);
    out.push({ label: label || `Option ${n}`, ht: Math.round(ht * 100) / 100 });
    notes.push(String(calc[`opt${n}Note`] ?? calc[`option${n}_note`] ?? calc[`option${n}Note`] ?? '').replace(/\s+/g, ' ').trim());
  });
  // Conserve la note libre saisie dans le calculateur, en l'ajoutant après le nettoyage du libellé.
  const noteByIndex = new Map(out.map((o, i) => [o, notes[i] || '']));
  const kept = uniqueOptions(out);
  return kept.map((o) => {
    const note = noteByIndex.get(o) || '';
    return note && !o.label.toLowerCase().includes(note.toLowerCase())
      ? { ...o, label: `${o.label} — ${note}` }
      : o;
  });
};

const extractContratOptionsFromNotes = (notes: any): ContratOption[] => {
  const raw = String(notes ?? '').trim();
  if (!raw) return [];
  const afterDistance = raw.split('—').pop() || raw;
  const out = afterDistance
    .split(/\s*[·;\n]\s*/)
    .map((part) => cleanOptionLabel(part.replace(/^options?\s*:?\s*/i, '')))
    .filter((label) => label && !/^distance\b/i.test(label))
    .map((label) => ({ label, ht: 0 }));
  return uniqueOptions(out);
};

const extractContratOptions = (rowOrCalc: any): ContratOption[] => {
  const calc = rowOrCalc?.calc_data ?? rowOrCalc?.calc ?? rowOrCalc;
  const fromCalc = extractContratOptionsFromCalc(calc);
  return fromCalc.length ? fromCalc : extractContratOptionsFromNotes(rowOrCalc?.notes);
};


// Construit le prochain numéro de devis prévisionnel (sans incrémenter le compteur)
async function fetchNextDevisNumero(): Promise<string> {
  const { data: em } = await supabase
    .from('emetteur')
    .select('prefix_devis, prochain_numero_devis')
    .limit(1)
    .maybeSingle();
  if (!em) return '';
  const num = String(em.prochain_numero_devis ?? 1).padStart(3, '0');
  return `${em.prefix_devis || ''}${num}`;
}

const fmtFR = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR');
};

const eur = (n: number | null | undefined) =>
  n == null ? '' : n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toMoneyNumber = (value: any) => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const buildEcheancierRowsFromProp = (prop: any, totalTTC = 0) => {
  const base = buildBaseEcheancierRows(prop, totalTTC);
  const overrides = Array.isArray(prop?.echeancier_overrides) ? prop.echeancier_overrides : [];
  if (!overrides.length) return base;
  // Overrides = corrections partielles par ligne (montants HT) : fusion avec l'échéancier calculé.
  const tvaRaw = Number(prop?.tva_pct);
  const factor = 1 + (Number.isFinite(tvaRaw) ? tvaRaw : 20) / 100;
  return base.map((r, i) => {
    const o = overrides[i] || {};
    const hasMt = o?.montant != null && o.montant !== '' && Number.isFinite(toMoneyNumber(o.montant));
    return {
      label: o?.label || r.label,
      date: o?.date || r.date,
      montant: hasMt ? Math.round(toMoneyNumber(o.montant) * factor * 100) / 100 : r.montant,
    };
  });
};

const buildBaseEcheancierRows = (prop: any, totalTTC = 0) => {

  const pct = Number(prop?.acompte_pct ?? 30);
  const jours = Number(prop?.acompte_jours ?? 15);
  const nb = Math.max(1, Number(prop?.nb_echeances ?? 1));
  const rows: { label: string; date: string; montant: number | null }[] = [];
  const today = new Date();
  const eventDate = prop?.date_event ? new Date(prop.date_event) : new Date();
  const acompte = totalTTC > 0 ? Math.round((totalTTC * pct) / 100 * 100) / 100 : null;
  const solde = totalTTC > 0 && acompte != null ? Math.max(0, totalTTC - acompte) : 0;

  if (pct > 0) {
    const d = new Date(today);
    d.setDate(d.getDate() + Math.max(0, jours));
    rows.push({ label: `Acompte (${pct}%)`, date: d.toISOString().slice(0, 10), montant: acompte });
  }

  let cumul = 0;
  for (let i = 1; i <= nb; i++) {
    const d = new Date(eventDate);
    d.setMonth(d.getMonth() + (i - nb));
    const isLast = i === nb;
    const montant = totalTTC > 0
      ? (isLast ? Math.round((solde - cumul) * 100) / 100 : Math.round((solde / nb) * 100) / 100)
      : null;
    if (montant != null) cumul += montant;
    rows.push({ label: isLast ? 'Solde' : `Traite ${i}/${nb - 1}`, date: d.toISOString().slice(0, 10), montant });
  }
  return rows;
};

const buildModalitesTextFromProp = (prop: any, fallbackMode?: string, totalTTC = 0): string => {
  const mode = (prop?.mode_paiement || fallbackMode || 'Virement bancaire') as string;
  const rows = buildEcheancierRowsFromProp(prop, totalTTC);
  const lignes: string[] = [`Mode de paiement : ${mode}`, 'Échéancier détaillé (montants TTC) :'];
  if (rows.length) {
    rows.forEach((r) => {
      const dt = r.date ? fmtFR(r.date) : '';
      const mt = r.montant != null ? `${eur(Number(r.montant))} € TTC` : '';
      lignes.push(`  • ${[r.label || 'Échéance', dt && `le ${dt}`, mt].filter(Boolean).join(' — ')}`);
    });
  } else {
    lignes.push('  • 30 % à la signature, solde le jour J');
  }
  return lignes.join('\n');
};

export default function NouveauContrat() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const data = useCrm();
  const prestationId = params.get('prestation');
  const initialPropositionId = params.get('proposition'); // permet de cibler V1 vs V2
  const autoAction = params.get('auto'); // 'propal-contrat' pour télécharger directement

  const [structure, setStructure] = useState<Structure>('cygnature');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadingMerged, setLoadingMerged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentClientId, setCurrentClientId] = useState<string | null>(null);
  const [propositionOptions, setPropositionOptions] = useState<PropOption[]>([]);
  const [selectedPropositionId, setSelectedPropositionId] = useState<string>('');

  // Quand la structure émettrice change, on aligne le préfixe de la réf. devis
  // sur celui de l'émetteur sélectionné (ex: P-SVR-2026-013 → P-CY-2026-013).
  useEffect(() => {
    (async () => {
      const { data: em } = await supabase
        .from('emetteur')
        .select('prefix_devis')
        .eq('code', structure)
        .maybeSingle();
      const newPrefix = (em as any)?.prefix_devis as string | undefined;
      if (!newPrefix) return;
      setForm((f) => {
        const cur = f.devis_numero || '';
        if (!cur) return f;
        const m = cur.match(/(\d+)\s*$/);
        const tail = m ? m[1] : '';
        if (!tail) return f;
        const next = `${newPrefix}${tail}`;
        return next === cur ? f : { ...f, devis_numero: next };
      });
    })();
  }, [structure]);


  // Construit le texte « Modalités de paiement » depuis une proposition
  const buildModalitesFromProp = (prop: any, fallbackMode?: string, totalTTC = 0): string =>
    buildModalitesTextFromProp(prop, fallbackMode, totalTTC);

  // Charge une proposition + (option) ses lignes, et met à jour le formulaire
  const applyProposition = async (propId: string, opts: { setLignes?: boolean } = {}) => {
    if (!propId) return null;
    const { data: prop, error } = await supabase
      .from('propositions').select('*').eq('id', propId).maybeSingle();
    if (error || !prop) { toast.error("Impossible de charger la proposition sélectionnée"); return null; }
    const { data: ls } = await supabase
      .from('propositions_lignes').select('*').eq('proposition_id', propId).order('ordre', { ascending: true });
    // Charge les options retenues des artistes liés pour les reprendre dans le contrat client.
    const artistIds = Array.from(new Set((ls || []).map((l: any) => l.artiste_id).filter(Boolean)));
    let detailsByArtist: Record<string, any> = {};
    if (artistIds.length && prop.prestation_id) {
      const { data: pa } = await supabase
        .from('prestations_artistes')
        .select('artiste_id, calc_data, notes')
        .eq('prestation_id', prop.prestation_id)
        .in('artiste_id', artistIds);
      (pa || []).forEach((r: any) => { if (r.artiste_id) detailsByArtist[r.artiste_id] = r; });
    }
    const lignesData = (ls || []).map((l: any) => ({
      titre: l.titre || '',
      description: l.description || '',
      quantite: Number(l.quantite) || 0,
      prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0,
      options: l.artiste_id ? extractContratOptions(detailsByArtist[l.artiste_id]) : [],
    }));
    const brutHT = lignesData.reduce((s, l) => s + l.quantite * l.prix_unitaire_ht, 0);
    const remisePct = Math.max(0, Math.min(100, Number((prop as any).remise_pct) || 0));
    const remiseMontant = Math.round(brutHT * (remisePct / 100) * 100) / 100;
    const totalHT = Math.round((brutHT - remiseMontant) * 100) / 100;

    const propTvaPct = (prop as any)?.tva_pct === null || (prop as any)?.tva_pct === undefined ? 20 : Number((prop as any).tva_pct);
    setForm((f) => {
      const tHT = totalHT;
      const tTVA = Math.round(tHT * (propTvaPct / 100) * 100) / 100;
      const tTTC = Math.round((tHT + tTVA) * 100) / 100;
      const updates: Partial<FormState> = {
        modalites_paiement: buildModalitesFromProp(prop, undefined, tTTC),
        echeancier_paiement: buildEcheancierRowsFromProp(prop, tTTC),
        devis_numero: prop.numero || f.devis_numero,
        devis_date: prop.date_emission ? fmtFR(prop.date_emission) : f.devis_date,
      };
      if (opts.setLignes || lignesData.length) {
        updates.proposition_numero = prop.numero || '';
        updates.proposition_lignes = lignesData;
        updates.proposition_total_ht = tHT;
        updates.proposition_total_tva = tTVA;
        updates.proposition_total_ttc = tTTC;

        if (tTTC > 0) {
          updates.montant_ht = eur(tHT);
          updates.montant_tva = eur(tTVA);
          updates.montant_ttc = eur(tTTC);
          updates.montant_ttc_lettres = montantEnLettres(tTTC).toUpperCase() + ' TTC';
        }
      }
      return { ...f, ...updates };
    });
    return prop;
  };

  // Charge la liste des propositions du client courant pour le sélecteur
  useEffect(() => {
    (async () => {
      if (!currentClientId) { setPropositionOptions([]); return; }
      const { data: rows } = await supabase
        .from('propositions')
        .select('id, numero, prestation_id, client_id, statut, date_emission, date_event, mode_paiement, acompte_pct, acompte_jours, nb_echeances, echeancier_overrides')
        .eq('client_id', currentClientId)
        .order('date_emission', { ascending: false });
      setPropositionOptions((rows as PropOption[]) || []);
    })();
  }, [currentClientId]);

  // Pré-remplissage depuis la prestation (si arrivé via lien)
  useEffect(() => {
    (async () => {
      const todayFR = new Date().toLocaleDateString('fr-FR');
      if (!prestationId) {
        // Aucune prestation liée : on auto-remplit la réf devis prévisionnelle + date du jour
        const nextNum = await fetchNextDevisNumero();
        setForm((f) => ({
          ...f,
          devis_numero: f.devis_numero || nextNum,
          devis_date: f.devis_date || todayFR,
        }));
        return;
      }
      const p = data.prest.find((x) => x.id === prestationId);
      if (!p) return;
      setStructure((p.structure as Structure) || 'cygnature');
      setCurrentClientId(p.clientId || null);

      // Charger client + dernier devis pour cette prestation
      let adresse = '', cpVille = '', rep = '', clientTel = '', clientEmail = '', clientSiret = '';
      if (p.clientId) {
        const { data: c } = await supabase.from('clients').select('*').eq('id', p.clientId).maybeSingle();
        if (c) {
          adresse = stripCpVille(c.adresse, c.cp, c.ville);
          cpVille = [c.cp, c.ville].filter(Boolean).join(' ');
          rep = [c.prenom, c.nom].filter(Boolean).join(' ');
          clientTel = c.tel || c.tel_secondaire || '';
          clientEmail = c.email || '';
          clientSiret = [c.siret, c.tva_intra].filter(Boolean).join(' — ');
        }
      }
      if (!clientTel) clientTel = (p as any).clientTel || '';
      if (!clientEmail) clientEmail = (p as any).clientEmail || '';

      // Contact sur place + contraintes techniques saisis dans le dossier
      let contactSurPlace = '', contraintesDossier = '';
      {
        const { data: pr } = await supabase
          .from('prestations')
          .select('contact_sur_place_nom, contact_sur_place_tel, contact_sur_place_email, contraintes_techniques')
          .eq('id', p.id).maybeSingle();
        if (pr) {
          contactSurPlace = [pr.contact_sur_place_nom, pr.contact_sur_place_tel, pr.contact_sur_place_email]
            .filter(Boolean).join(' — ');
          contraintesDossier = pr.contraintes_techniques || '';
        }
      }
      const { data: devisRows } = await supabase
        .from('devis').select('*').eq('prestation_id', p.id)
        .order('date_emission', { ascending: false }).limit(1);
      const d = devisRows?.[0];

      // Récupère la proposition liée (pour les modalités de paiement)
      // Stratégie : on prend la plus récente NON brouillon liée à la prestation.
      // Fallback : si rien (prestation_id non renseigné), on cherche par client_id + date_event.
      let prop: any = null;
      // Si une proposition spécifique est passée dans l'URL (?proposition=...),
      // on l'utilise telle quelle (permet de cibler V1 vs V2).
      if (initialPropositionId) {
        const { data: forced } = await supabase
          .from('propositions').select('*').eq('id', initialPropositionId).maybeSingle();
        if (forced) prop = forced;
      }
      if (!prop) {
        const { data: propRows } = await supabase
          .from('propositions').select('*')
          .eq('prestation_id', p.id)
          .neq('statut', 'Brouillon')
          .order('date_emission', { ascending: false }).limit(1);
        prop = propRows?.[0] || null;

        if (!prop) {
          // fallback inclus brouillons (au cas où aucune n'est encore envoyée)
          const { data: anyRows } = await supabase
            .from('propositions').select('*')
            .eq('prestation_id', p.id)
            .order('updated_at', { ascending: false }).limit(1);
          prop = anyRows?.[0] || null;
        }

        if (!prop && p.clientId && p.dateEvent) {
          // fallback par client + date d'événement
          const { data: byClient } = await supabase
            .from('propositions').select('*')
            .eq('client_id', p.clientId)
            .eq('date_event', p.dateEvent)
            .order('updated_at', { ascending: false }).limit(1);
          prop = byClient?.[0] || null;
        }
      }

      if (!prop) {
        console.warn('[NouveauContrat] Aucune proposition trouvée pour prestation', p.id);
      } else {
        console.log('[NouveauContrat] Proposition liée trouvée', prop.numero, 'overrides:', prop.echeancier_overrides);
        setSelectedPropositionId(prop.id);
      }

      let ht = 0, tva = 0, ttc = 0;
      let propLignesData: { titre: string; description: string; quantite: number; prix_unitaire_ht: number; options?: { label: string; ht: number }[] }[] = [];
      if (prop?.id) {
        const { data: propLignes } = await supabase
          .from('propositions_lignes')
          .select('titre, description, quantite, prix_unitaire_ht, artiste_id')
          .eq('proposition_id', prop.id)
          .order('ordre', { ascending: true });
        // Récupère les options retenues par artiste (calc_data actuel + notes legacy).
        const artistIds = Array.from(new Set((propLignes || []).map((l: any) => l.artiste_id).filter(Boolean)));
        const detailsByArtist: Record<string, any> = {};
        if (artistIds.length && prop.prestation_id) {
          const { data: pa } = await supabase
            .from('prestations_artistes')
            .select('artiste_id, calc_data, notes')
            .eq('prestation_id', prop.prestation_id)
            .in('artiste_id', artistIds);
          (pa || []).forEach((r: any) => { if (r.artiste_id) detailsByArtist[r.artiste_id] = r; });
        }
        propLignesData = (propLignes || []).map((l: any) => ({
          titre: l.titre || '',
          description: l.description || '',
          quantite: Number(l.quantite) || 0,
          prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0,
          options: l.artiste_id ? extractContratOptions(detailsByArtist[l.artiste_id]) : [],
        }));
        const brut = propLignesData.reduce((s, l) => s + l.quantite * l.prix_unitaire_ht, 0);
        const remisePct = Math.max(0, Math.min(100, Number((prop as any).remise_pct) || 0));
        ht = Math.round((brut - brut * (remisePct / 100)) * 100) / 100;
        const propTvaPct = (prop as any)?.tva_pct === null || (prop as any)?.tva_pct === undefined ? 20 : Number((prop as any).tva_pct);
        tva = Math.round(ht * (propTvaPct / 100) * 100) / 100;
        ttc = Math.round((ht + tva) * 100) / 100;

      }

      // Matériel / locations : reprend les produits liés au dossier
      let materielTxt = '';
      const prestDbId = prop?.prestation_id || null;
      if (prestDbId) {
        const { data: prods } = await supabase
          .from('prestations_produits')
          .select('libelle, quantite, ordre')
          .eq('prestation_id', prestDbId)
          .order('ordre', { ascending: true });
        materielTxt = (prods || [])
          .map((r: any) => {
            const q = Number(r.quantite) || 1;
            return q > 1 ? `${r.libelle} × ${q}` : String(r.libelle || '');
          })
          .filter(Boolean)
          .join('\n');
      }


      if (!ht && d?.id) {
        const { data: lignes } = await supabase.from('devis_lignes').select('*').eq('devis_id', d.id);
        for (const l of lignes || []) {
          const lht = (l.quantite || 0) * (l.prix_unitaire_ht || 0);
          ht += lht;
          tva += lht * ((l.tva_pct || 0) / 100);
        }
        ttc = ht + tva;
      }
      if (!ht && p.propTTC) {
        ttc = p.propTTC;
        ht = ttc / 1.2; tva = ttc - ht;
      }

      const modalites = buildModalitesFromProp(prop, d?.mode_paiement, ttc);

      const devisNum = prop?.numero || d?.numero || (await fetchNextDevisNumero());
      const devisDateIso = prop?.date_emission || d?.date_emission || null;
      setForm((f) => ({
        ...f,
        client_nom: p.client || '',
        client_adresse: adresse,
        client_cp_ville: cpVille,
        client_representant: rep,
        client_tel: clientTel,
        client_email: clientEmail,
        client_siret: clientSiret,
        contact_sur_place: contactSurPlace,
        contraintes: contraintesDossier || f.contraintes,
        devis_numero: devisNum,
        devis_date: devisDateIso ? fmtFR(devisDateIso) : new Date().toLocaleDateString('fr-FR'),
        event_intitule: (p.themes || []).join(', ') || p.libelle || '',
        date_debut: fmtFR(p.dateEvent),
        date_fin: fmtFR(p.dateEvent),
        lieu: p.lieu || '',
        cb_artistique: false,
        cb_services: false,
        cb_location: !!materielTxt,
        type_event: (p.themes || []).join(', ') || p.libelle || '',
        type_domaine: (p.domaines || []).join(', '),
        services: (p.services || []).join(', '),
        materiel: materielTxt,
        montant_ht: eur(ht),
        montant_tva: eur(tva),
        montant_ttc: eur(ttc),
        montant_ttc_lettres: ttc > 0 ? montantEnLettres(ttc).toUpperCase() + ' TTC' : '',
        modalites_paiement: modalites,
        echeancier_paiement: buildEcheancierRowsFromProp(prop, ttc),
        proposition_numero: prop?.numero || '',
        proposition_lignes: propLignesData,
        proposition_total_ht: ht,
        proposition_total_tva: tva,
        proposition_total_ttc: ttc,
        nom_signataire_client: p.client || '',
      }));
    })();
  }, [prestationId, data.prest]);

  // Auto-recalcule la somme en lettres dès que le TTC change
  const parsedTtc = useMemo(() => {
    const n = parseFloat((form.montant_ttc || '').replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }, [form.montant_ttc]);
  useEffect(() => {
    if (parsedTtc > 0) {
      const lettres = montantEnLettres(parsedTtc).toUpperCase() + ' TTC';
      setForm((f) => (f.montant_ttc_lettres === lettres ? f : { ...f, montant_ttc_lettres: lettres }));
    }
  }, [parsedTtc]);
  // Si le chèque de garantie (règlement échelonné) est coché, son montant suit le total TTC
  useEffect(() => {
    if (!form.cb_cheque_garantie) return;
    setForm((f) => (f.cheque_garantie_montant === f.montant_ttc ? f : { ...f, cheque_garantie_montant: f.montant_ttc }));
  }, [form.cb_cheque_garantie, form.montant_ttc]);



  // Auto-déclenche la génération Propal+Contrat si demandé via l'URL (?auto=propal-contrat)
  const [autoTriggered, setAutoTriggered] = useState(false);
  useEffect(() => {
    if (autoTriggered) return;
    if (autoAction !== 'propal-contrat') return;
    if (!selectedPropositionId) return;
    if (!form.client_nom) return; // attend que le prefill ait posé le client
    setAutoTriggered(true);
    // léger délai pour s'assurer que tout le state est posé
    setTimeout(() => { genererPropalEtContrat(); }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAction, selectedPropositionId, form.client_nom, autoTriggered]);

  // Garde-fou : si aucune proposition n'a pu être liée, on n'attend pas indéfiniment.
  useEffect(() => {
    if (autoAction !== 'propal-contrat') return;
    const t = setTimeout(() => {
      if (!autoTriggered) {
        toast.error("Aucune proposition liée à ce dossier — synchronisation impossible.");
        navigate('/crm-pro-evenementiel/contrats');
      }
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAction, autoTriggered]);


  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const generer = async () => {
    setLoading(true);
    try {
      const slug = (form.client_nom || 'sans-nom').toLowerCase().replace(/[^a-z0-9-]+/g, '-');
      await genererContratPdf(structure, form, `contrat-${structure}-${slug}.pdf`);
      toast.success('Contrat PDF généré');
    } catch (e: any) {
      toast.error(`Échec : ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const apercu = () => {
    try {
      previewContratHtml(structure, form);
    } catch (e: any) {
      toast.error(`Échec aperçu : ${e.message || e}`);
    }
  };

  // Enregistre le contrat en base (table `contrats`) avec auto-numérotation
  const saveContrat = async () => {
    if (!form.client_nom?.trim()) {
      toast.error('Renseigne au moins le nom du client.');
      return;
    }
    setSaving(true);
    try {
      // 1. Récupère l'émetteur correspondant à la structure pour générer le numéro
      const { data: em, error: emErr } = await supabase
        .from('emetteur')
        .select('id, prefix_contrat, prochain_numero_contrat')
        .eq('code', structure)
        .maybeSingle();
      if (emErr || !em) throw new Error("Émetteur introuvable pour cette structure");

      const prefix = (em as any).prefix_contrat || 'C-2026-';
      const next = Number((em as any).prochain_numero_contrat || 1);
      const numero = `${prefix}${String(next).padStart(4, '0')}`;

      // 2. Construit le payload
      const ttcNum = parseFloat((form.montant_ttc || '').replace(/\s/g, '').replace(',', '.'));
      const htNum = parseFloat((form.montant_ht || '').replace(/\s/g, '').replace(',', '.'));
      // Le param `prestation` dans l'URL est le numéro/dossier (ex: 261031/LEALOU/JAMET/002),
      // pas l'UUID DB. On résout le vrai UUID avant l'insert pour éviter l'erreur
      // "invalid input syntax for type uuid".
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      let prestationUuid: string | null = null;
      if (prestationId) {
        if (UUID_RE.test(prestationId)) {
          prestationUuid = prestationId;
        } else {
          const { data: prestRow } = await supabase
            .from('prestations')
            .select('id')
            .eq('numero', prestationId)
            .maybeSingle();
          prestationUuid = (prestRow as any)?.id || null;
        }
      }
      const payload: any = withDemoFlag({
        numero,
        structure,
        prestation_id: prestationUuid,
        proposition_id: selectedPropositionId && UUID_RE.test(selectedPropositionId) ? selectedPropositionId : null,
        client_id: currentClientId && UUID_RE.test(currentClientId) ? currentClientId : null,
        client_nom: form.client_nom,
        date_emission: new Date().toISOString().slice(0, 10),
        date_event: frToIso(form.date_debut),
        date_signature: frToIso(form.date_signature),
        statut: 'Brouillon',
        montant_ttc: Number.isFinite(ttcNum) ? ttcNum : null,
        montant_ht: Number.isFinite(htNum) ? htNum : null,
        objet: form.event_intitule || null,
        contenu: JSON.stringify(form),
      });

      // 3. Insert + incrément du compteur
      const { error: insErr } = await supabase.from('contrats').insert(payload);
      if (insErr) throw insErr;
      await supabase.from('emetteur').update({ prochain_numero_contrat: next + 1 }).eq('id', (em as any).id);

      toast.success(`Contrat ${numero} enregistré`);
      navigate('/crm-pro-evenementiel/propositions-contrats?tab=contrats&subtab=transmis');
    } catch (e: any) {
      console.error('[saveContrat]', e);
      toast.error(`Échec enregistrement : ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };


  // Génère un PDF unique = la Proposition (visuel identique au PDF Proposition)
  // SUIVIE du Contrat (visuel identique au PDF Contrat). Les deux documents sont
  // rendus indépendamment puis empilés dans le même PDF — aucune transformation.
  const genererPropalEtContrat = async () => {
    if (!selectedPropositionId) {
      toast.error("Sélectionne d'abord une proposition à intégrer au contrat.");
      return;
    }
    setLoadingMerged(true);
    try {
      // 1. Synchronise les modalités du contrat avec la proposition choisie.
      const prop = await applyProposition(selectedPropositionId, { setLignes: true });
      if (!prop) return;
      await new Promise((r) => setTimeout(r, 50));

      // 2. Charge la proposition complète (lignes + client) pour le rendu.
      const [{ data: pRow }, { data: ls }] = await Promise.all([
        supabase.from('propositions').select('*').eq('id', selectedPropositionId).maybeSingle(),
        supabase.from('propositions_lignes').select('*').eq('proposition_id', selectedPropositionId).order('ordre', { ascending: true }),
      ]);
      if (!pRow) throw new Error('Proposition introuvable');
      const proposition: Proposition = rowToProp(pRow, ls || []);

      let clientRecord: any = null;
      if (pRow.client_id) {
        const { data: c } = await supabase.from('clients').select('*').eq('id', pRow.client_id).maybeSingle();
        clientRecord = c || null;
      }

      // 3. Totaux + échéancier (réplique de la logique de l'onglet Propositions).
      const lignes = proposition.lignes;
      const brutHT = lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT, 0);
      const remisePct = Math.max(0, Math.min(100, Number((proposition as any).remise_pct) || 0));
      const remiseMontant = Math.round(brutHT * (remisePct / 100) * 100) / 100;
      const totalHT = Math.round((brutHT - remiseMontant) * 100) / 100;
      const tvaPct = (pRow as any)?.tva_pct === null || (pRow as any)?.tva_pct === undefined ? 20 : Number((pRow as any).tva_pct);
      const totalTVA = Math.round(totalHT * (tvaPct / 100) * 100) / 100;
      const totalTTC = Math.round((totalHT + totalTVA) * 100) / 100;


      const nb = Math.max(1, Math.min(24, proposition.nb_echeances || 1));
      const acomptePct = Math.max(0, Math.min(100, proposition.acompte_pct || 0));
      const acompte = Math.round((totalHT * acomptePct) / 100);
      const solde = Math.max(0, totalHT - acompte);
      const refDate = proposition.date_event ? new Date(proposition.date_event) : new Date();
      const rows: { label: string; date: string; montant: number }[] = [];
      if (acompte > 0) {
        const aDate = new Date();
        aDate.setDate(aDate.getDate() + Math.max(0, proposition.acompte_jours || 0));
        rows.push({ label: `Acompte (${acomptePct}%)`, date: aDate.toISOString().slice(0, 10), montant: acompte });
      }
      let cumul = 0;
      const base = solde / nb;
      for (let i = 1; i <= nb; i++) {
        const d = new Date(refDate);
        d.setMonth(d.getMonth() + (i - nb));
        const isLast = i === nb;
        const montant = isLast ? Math.round(solde - cumul) : Math.round(base);
        cumul += montant;
        const label = isLast ? 'Solde' : (nb > 1 ? `Traite ${i}/${nb - 1}` : 'Solde');
        rows.push({ label, date: d.toISOString().slice(0, 10), montant });
      }
      const overrides = proposition.echeancier_overrides || [];
      const merged = rows.map((r, i) => {
        const o = overrides[i] || {};
        return {
          label: o.label ?? r.label,
          date: o.date ?? r.date,
          montant: typeof o.montant === 'number' && !Number.isNaN(o.montant) ? o.montant : r.montant,
        };
      });
      const finalAcompte = merged.length && acompte > 0 ? merged[0].montant : 0;
      const finalSolde = merged.slice(acompte > 0 ? 1 : 0).reduce((s, r) => s + (r.montant || 0), 0);
      const echeancier = { acompte: finalAcompte, solde: finalSolde, rows: merged };

      // 4. HTML "Proposition" — identique au PDF Proposition.
      const slug = (form.client_nom || proposition.client_nom || 'sans-nom').toLowerCase().replace(/[^a-z0-9-]+/g, '-');

      // Charge les calc_data des artistes liés pour récupérer les options
      const artistIds = Array.from(new Set(lignes.map((l) => l.artisteId).filter(Boolean) as string[]));
      const calcByArtistMap: Record<string, { calc: any; notes: string | null; km: number | null; cout: number | null; prop_ttc: number | null; marge_pct: number | null }> = {};
      if (artistIds.length && pRow.prestation_id) {
        const { data: pa } = await supabase
          .from('prestations_artistes')
          .select('artiste_id, km, cout, prop_ttc, marge_pct, calc_data, notes')
          .eq('prestation_id', pRow.prestation_id)
          .in('artiste_id', artistIds);
        (pa || []).forEach((r: any) => {
          if (r.artiste_id) calcByArtistMap[r.artiste_id] = { calc: r.calc_data, notes: r.notes, km: r.km, cout: r.cout, prop_ttc: r.prop_ttc, marge_pct: r.marge_pct };
        });
      }

      const propHtml = renderPropositionHTML(
        proposition,
        totalHT,
        echeancier,
        `proposition-${slug}.pdf`,
        {
          tvaPct,
          remise_pct: proposition.remise_pct,
          remarques: proposition.remarques || '',
          calcByArtist: calcByArtistMap,
          visuels: [
            { dataUrl: proposition.visuel_data_url, name: proposition.visuel_name },
            { dataUrl: proposition.visuel2_data_url, name: proposition.visuel2_name },
            { dataUrl: proposition.visuel3_data_url, name: proposition.visuel3_name },
          ],
          client: clientRecord,
        },
      );

      // 5. HTML "Contrat" — identique au PDF Contrat (avec les bons totaux).
      const lignesForContrat = lignes.map((l) => ({
        titre: l.titre,
        description: l.description,
        quantite: l.quantite,
        prix_unitaire_ht: l.prixUnitaireHT,
        options: l.artisteId ? extractContratOptions(calcByArtistMap[l.artisteId]) : [],
      }));
      const dataForContrat = {
        ...form,
        modalites_paiement: buildModalitesFromProp(pRow, undefined, totalTTC),
        echeancier_paiement: buildEcheancierRowsFromProp(pRow, totalTTC),
        proposition_numero: proposition.numero || '',
        proposition_lignes: lignesForContrat,
        proposition_total_ht: totalHT,
        proposition_total_tva: totalTVA,
        proposition_total_ttc: totalTTC,
        montant_ht: eur(totalHT),
        montant_tva: eur(totalTVA),
        montant_ttc: eur(totalTTC),
        montant_ttc_lettres: totalTTC > 0 ? montantEnLettres(totalTTC).toUpperCase() + ' TTC' : '',
      };
      const contratHtml = renderContratHTML(structure, dataForContrat);

      // 6. Empile les deux dans un seul PDF, sans toucher au rendu.
      await exportHtmlsToPdf([propHtml, contratHtml], {
        fileName: `propal-contrat-${structure}-${slug}.pdf`,
        rootId: '__propal_contrat_pdf_root',
      });
      toast.success('PDF Propal+Contrat généré');
    } catch (e: any) {
      toast.error(`Échec : ${e.message || e}`);
    } finally {
      setLoadingMerged(false);
      if (autoAction === 'propal-contrat') {
        // Retour automatique à la liste : l'utilisateur n'a jamais à voir l'éditeur de contrat
        setTimeout(() => navigate('/crm-pro-evenementiel/contrats'), 300);
      }
    }
  };



  // Mode auto « Propal+Contrat » : on masque l'éditeur et on affiche juste un loader,
  // pour que le clic sur le bouton « Propal+Contrat » télécharge sans jamais ouvrir l'écran de prépa.
  if (autoAction === 'propal-contrat') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-300">
        <Loader2 size={28} className="animate-spin text-amber-300" strokeWidth={1.5} />
        <div className="text-sm">Génération du PDF Propal + Contrat…</div>
        <div className="text-xs text-slate-500">Tu vas être redirigé automatiquement.</div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/crm-pro-evenementiel/contrats" className="inline-flex items-center gap-1.5 text-xs text-slate-400 mb-2">
        <ArrowLeft size={12} strokeWidth={1.5} /> Retour aux contrats
      </Link>
      <PageTitle title="Nouveau contrat" subtitle="Génère un PDF prêt à signer" icon={<FileText size={24} className="text-emerald-400" strokeWidth={1.5} />} />


      <Card className="p-4 mb-4">
        <div className="text-xs text-slate-400 mb-2 font-medium">Structure émettrice</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {STRUCTURE_OPTIONS.map((o) => (
            <button key={o.code} onClick={() => setStructure(o.code)}
              className={`text-left p-3 rounded-lg border ${structure === o.code ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-slate-700 bg-slate-900'}`}>
              <div className="text-sm text-slate-100 font-medium">{o.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{o.sub}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4 mb-4">
        <div className="text-xs text-slate-400 mb-2 font-medium">Proposition liée (modalités de paiement + annexe PDF)</div>
        {propositionOptions.length === 0 ? (
          <div className="text-xs text-slate-500 italic">Aucune proposition trouvée pour ce client.</div>
        ) : (
          <div className="flex flex-col gap-2">
            <select
              value={selectedPropositionId}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedPropositionId(v);
                if (v) applyProposition(v);
              }}
              className="px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm text-slate-100 focus:border-emerald-500/50 focus:outline-none"
            >
              <option value="">— Choisir une proposition —</option>
              {propositionOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {(p.numero || p.id.slice(0, 8))} · {p.statut}
                  {p.date_event ? ` · événement ${fmtFR(p.date_event)}` : ''}
                  {p.date_emission ? ` · émise ${fmtFR(p.date_emission)}` : ''}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-slate-500">
              Le choix recharge les <em>Modalités de paiement</em>. Le bouton <strong>Propal+Contrat</strong> ajoute en plus le détail des lignes en annexe.
            </div>
          </div>
        )}
      </Card>


      <Section title="Client">
        <Field label="Nom / Raison sociale" v={form.client_nom} on={(v) => set('client_nom', v)} />
        <Field label="Adresse" v={form.client_adresse} on={(v) => set('client_adresse', v)} />
        <Field label="Code postal + Ville" v={form.client_cp_ville} on={(v) => set('client_cp_ville', v)} />
        <Field label="Nom du représentant" v={form.client_representant} on={(v) => set('client_representant', v)} />
        <Field label="Téléphone client" v={form.client_tel} on={(v) => set('client_tel', v)} />
        <Field label="E-mail client" v={form.client_email} on={(v) => set('client_email', v)} />
        <Field label="SIRET / TVA (si professionnel)" v={form.client_siret} on={(v) => set('client_siret', v)} full />
      </Section>

      <Section title="Devis & événement">
        <Field label="Référence devis" v={form.devis_numero} on={(v) => set('devis_numero', v)} />
        <Field label="Date du devis (JJ/MM/AAAA)" v={form.devis_date} on={(v) => set('devis_date', v)} />
        <Field label="Intitulé de l'événement" v={form.event_intitule} on={(v) => set('event_intitule', v)} full />
        <Field label="Date de début" v={form.date_debut} on={(v) => set('date_debut', v)} />
        <Field label="Heure de début" v={form.heure_debut} on={(v) => set('heure_debut', v)} />
        <Field label="Date de fin" v={form.date_fin} on={(v) => set('date_fin', v)} />
        <Field label="Heure de fin" v={form.heure_fin} on={(v) => set('heure_fin', v)} />
        <Field label="Lieu d'exécution" v={form.lieu} on={(v) => set('lieu', v)} full />
        <Field label="Contact sur place (nom, fonction, téléphone)" v={form.contact_sur_place} on={(v) => set('contact_sur_place', v)} full />
      </Section>

      <Section title="Type de prestation (article 3 — nature retenue)">
        <Check label="Services / coordination / organisation" v={form.cb_services} on={(v) => set('cb_services', v)} />
        <Check label="Prestation artistique / production / diffusion" v={form.cb_artistique} on={(v) => set('cb_artistique', v)} />
        <Check label="Décoration / scénographie" v={form.cb_decoration} on={(v) => set('cb_decoration', v)} />
        <Check label="Location de matériel" v={form.cb_location} on={(v) => set('cb_location', v)} />
        <Check label="Photobooth / animation photo" v={form.cb_photobooth} on={(v) => set('cb_photobooth', v)} />
        <Check label="Sonorisation / éclairage / technique" v={form.cb_technique} on={(v) => set('cb_technique', v)} />
        <Field label="Autre (préciser — coche automatiquement la case)" v={form.autre_nature} on={(v) => set('autre_nature', v)} full />
      </Section>

      <Section title="Détail prestations">
        <Area label="Type d'événement" v={form.type_event} on={(v) => set('type_event', v)} />
        <Area label="Type de domaine artistique" v={form.type_domaine} on={(v) => set('type_domaine', v)} />
        <Area label="Matériel loué" v={form.materiel} on={(v) => set('materiel', v)} />
        <Area label="Services inclus" v={form.services} on={(v) => set('services', v)} />
        <Area label="Exclus (non compris dans la prestation)" v={form.exclus} on={(v) => set('exclus', v)} />
        <Area label="Livrables" v={form.livrables} on={(v) => set('livrables', v)} />
        <Area label="Contraintes techniques / accessibilité" v={form.contraintes} on={(v) => set('contraintes', v)} />
      </Section>

      <Section title="Conditions techniques, accès et sécurité (article 5)">
        <Area label="Accès" v={form.acces} on={(v) => set('acces', v)} />
        <Area label="Stationnement" v={form.stationnement} on={(v) => set('stationnement', v)} />
        <Area label="Électricité" v={form.electricite} on={(v) => set('electricite', v)} />
        <Area label="Stockage" v={form.stockage} on={(v) => set('stockage', v)} />
      </Section>

      <Section title="Rôle contractuel de CYGNATURE (article 7)">
        <Check label="Producteur / employeur des artistes" v={form.role_producteur} on={(v) => set('role_producteur', v)} />
        <Check label="Diffuseur / organisateur artistique" v={form.role_diffuseur} on={(v) => set('role_diffuseur', v)} />
        <Check label="Prestataire direct" v={form.role_prestataire} on={(v) => set('role_prestataire', v)} />
        <Check label="Intermédiaire / coordinateur" v={form.role_intermediaire} on={(v) => set('role_intermediaire', v)} />
        <Check label="Loueur de matériel" v={form.role_loueur} on={(v) => set('role_loueur', v)} />
        <Area label="Rôle retenu et répartition des responsabilités (laisser vide = texte type)" v={form.role_detail} on={(v) => set('role_detail', v)} />
      </Section>

      <Section title="Photobooth (article 9 — si retenu)">
        <Field label="Formule" v={form.photobooth_formule} on={(v) => set('photobooth_formule', v)} />
        <Field label="Option IA" v={form.photobooth_ia} on={(v) => set('photobooth_ia', v)} />
        <Area label="Personnalisation" v={form.photobooth_personnalisation} on={(v) => set('photobooth_personnalisation', v)} />
        <Area label="Installation" v={form.photobooth_installation} on={(v) => set('photobooth_installation', v)} />
      </Section>


      <Section title="Montants">
        <Field label="Montant HT" v={form.montant_ht} on={(v) => set('montant_ht', v)} />
        <Field label="Montant TVA" v={form.montant_tva} on={(v) => set('montant_tva', v)} />
        <Field label="Montant TTC" v={form.montant_ttc} on={(v) => set('montant_ttc', v)} />
        <Field label="Montant TTC en lettres (auto)" v={form.montant_ttc_lettres} on={(v) => set('montant_ttc_lettres', v)} />
        <Area label="Modalités de paiement (auto depuis la proposition)" v={form.modalites_paiement} on={(v) => set('modalites_paiement', v)} />
        <div className="md:col-span-2 flex gap-4 items-center">
          <span className="text-xs text-slate-400">Option nettoyage (machine pop-corn) :</span>
          <Check label="Oui (15 €/machine)" v={form.cb_nettoyage_oui} on={(v) => { set('cb_nettoyage_oui', v); if (v) set('cb_nettoyage_non', false); }} inline />
          <Check label="Non" v={form.cb_nettoyage_non} on={(v) => { set('cb_nettoyage_non', v); if (v) set('cb_nettoyage_oui', false); }} inline />
        </div>
      </Section>

      <Section title="Caution location de matériel (dépôt de garantie) & pièce d'identité">
        <div className="md:col-span-2 text-xs text-slate-400">
          Concerne uniquement la <strong>location de matériel</strong> : dépôt de garantie restitué après contrôle du matériel rendu.
        </div>
        <Field label="Montant caution location (€) — par article ou global" v={form.caution} on={(v) => set('caution', v)} />
        <Field label="Forme de la caution" v={form.caution_forme} on={(v) => set('caution_forme', v)} />
        <Field label="Restitution" v={form.caution_restitution} on={(v) => set('caution_restitution', v)} full />
        <Field label="Dépôt de garantie — texte affiché au contrat (vide = 1 000 € pour Location Lumières et Rideaux/Fonds de scène)" v={(form as any).depot_garantie_texte || ''} on={(v) => set('depot_garantie_texte' as any, v as any)} full />
        <Field label="Remise du matériel (vide = date, lieu et contact de l'événement)" v={(form as any).remise_texte || ''} on={(v) => set('remise_texte' as any, v as any)} />
        <Field label="Retour du matériel (vide = date, lieu et contact de l'événement)" v={(form as any).retour_texte || ''} on={(v) => set('retour_texte' as any, v as any)} />
        <div className="md:col-span-2 flex flex-wrap gap-4 items-center">
          <span className="text-xs text-slate-400">Pièce d'identité :</span>
          <Check label="Vérifiée" v={!!(form as any).cb_pi_verifiee} on={(v) => set('cb_pi_verifiee' as any, v as any)} inline />
          <Check label="Copie autorisée et conservée" v={!!(form as any).cb_pi_copie} on={(v) => set('cb_pi_copie' as any, v as any)} inline />
          <Check label="Non requise" v={!!(form as any).cb_pi_non} on={(v) => set('cb_pi_non' as any, v as any)} inline />
        </div>
        <div className="md:col-span-2 flex flex-wrap gap-4 items-center">
          <span className="text-xs text-slate-400">Nettoyage :</span>
          <Check label="Inclus" v={!!(form as any).cb_nettoyage_inclus} on={(v) => set('cb_nettoyage_inclus' as any, v as any)} inline />
          <Check label="Client" v={form.cb_nettoyage_non} on={(v) => set('cb_nettoyage_non', v)} inline />
          <Check label="Option" v={form.cb_nettoyage_oui} on={(v) => set('cb_nettoyage_oui', v)} inline />
          <Field label="Montant option (€)" v={(form as any).nettoyage_montant || ''} on={(v) => set('nettoyage_montant' as any, v as any)} />
        </div>
      </Section>

      <Section title="Chèque de garantie (prestation de services / règlement échelonné)">
        <div className="md:col-span-2 space-y-2">
          <Check
            label="Chèque de garantie * (montant = total TTC du contrat)"
            v={form.cb_cheque_garantie}
            on={(v) => {
              set('cb_cheque_garantie', v);
              set('cheque_garantie_montant', v ? (form.montant_ttc || '') : '');
            }}
            inline
          />
          <p className="text-xs leading-relaxed text-slate-400">
            Texte au contrat : « Chèque de garantie correspondant au montant total du devis et du contrat, sera demandé à titre de garantie. »
          </p>
        </div>
        <Field label="Montant du chèque de garantie (€) — auto : total TTC" v={form.cheque_garantie_montant} on={(v) => set('cheque_garantie_montant', v)} full />
      </Section>



      <Section title="Logistique">
        <Area label="Autorisations / déclarations" v={form.autorisations} on={(v) => set('autorisations', v)} />
        <Field label="Collations" v={form.collations} on={(v) => set('collations', v)} />
        <Field label="Repas" v={form.repas} on={(v) => set('repas', v)} />
        <Field label="Hébergements" v={form.hebergements} on={(v) => set('hebergements', v)} />
        <Field label="Loge / espace privé" v={form.loge_espace} on={(v) => set('loge_espace', v)} full />
        <Area label="Particularités" v={form.particularites} on={(v) => set('particularites', v)} />
      </Section>

      <Section title="Signature">
        <Field label="Date de signature" v={form.date_signature} on={(v) => set('date_signature', v)} />
        <Field label="Nom et prénom du signataire client" v={form.nom_signataire_client} on={(v) => set('nom_signataire_client', v)} />
      </Section>

      <div className="flex justify-end gap-2 mt-4 flex-wrap">
        <button onClick={() => navigate('/crm-pro-evenementiel/contrats')}
          className="px-4 py-2 rounded-lg text-sm bg-slate-800 border border-slate-700 text-slate-300">Annuler</button>
        <button onClick={apercu}
          className="px-4 py-2 rounded-lg text-sm bg-slate-900/60 border border-slate-700 text-slate-300 flex items-center gap-2">
          <Eye size={14} strokeWidth={1.5} /> Aperçu HTML
        </button>
        <button onClick={saveContrat} disabled={saving}
          className="px-4 py-2 rounded-lg text-sm bg-sky-500/20 border border-sky-500/40 text-sky-300 flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" strokeWidth={1.5} /> : <Save size={14} strokeWidth={1.5} />}
          Enregistrer le contrat
        </button>
        <button onClick={generer} disabled={loading}
          className="px-4 py-2 rounded-lg text-sm bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center gap-2 disabled:opacity-50">
          {loading ? <Loader2 size={14} className="animate-spin" strokeWidth={1.5} /> : <Download size={14} strokeWidth={1.5} />}
          Télécharger le contrat
        </button>
        <button onClick={genererPropalEtContrat} disabled={loadingMerged || !selectedPropositionId}
          title={!selectedPropositionId ? 'Sélectionne une proposition liée ci-dessus' : ''}
          className="px-4 py-2 rounded-lg text-sm bg-amber-500/20 border border-amber-500/40 text-amber-300 flex items-center gap-2 disabled:opacity-50">
          {loadingMerged ? <Loader2 size={14} className="animate-spin" strokeWidth={1.5} /> : <Download size={14} strokeWidth={1.5} />}
          Télécharger Propal+Contrat
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-4 mb-3">
      <div className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wide">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </Card>
  );
}

function Field({ label, v, on, full }: { label: string; v: string; on: (v: string) => void; full?: boolean }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? 'md:col-span-2' : ''}`}>
      <span className="text-xs text-slate-400">{label}</span>
      <input value={v} onChange={(e) => on(e.target.value)}
        className="px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm text-slate-100 focus:border-emerald-500/50 focus:outline-none" />
    </label>
  );
}

function Area({ label, v, on }: { label: string; v: string; on: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1 md:col-span-2">
      <span className="text-xs text-slate-400">{label}</span>
      <textarea value={v} onChange={(e) => on(e.target.value)} rows={2}
        className="px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm text-slate-100 focus:border-emerald-500/50 focus:outline-none resize-y" />
    </label>
  );
}

function Check({ label, v, on, inline }: { label: string; v: boolean; on: (v: boolean) => void; inline?: boolean }) {
  return (
    <label className={`flex items-center gap-2 ${inline ? '' : 'p-2 rounded-md bg-slate-900 border border-slate-700 cursor-pointer'}`}>
      <input type="checkbox" checked={v} onChange={(e) => on(e.target.checked)} className="accent-emerald-500" />
      <span className="text-sm text-slate-200">{label}</span>
    </label>
  );
}
